/**
 * Firebase Authentication Service
 * Handles Firebase ID Token management for backend API authentication
 *
 * Usage:
 *   import { getFirebaseToken, onAuthStateChange, signInAnonymously } from './services/firebaseAuth';
 *
 *   // Get token for API calls
 *   const token = await getFirebaseToken();
 *
 *   // Listen for auth state changes
 *   onAuthStateChange((user) => {
 *     if (user) console.log('Logged in:', user.uid);
 *   });
 */

import {
  initializeApp,
  getApps,
  getApp
} from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  getIdToken,
  connectAuthEmulator
} from 'firebase/auth';

// Firebase configuration from build environment
// These are injected during build process
const firebaseConfig = {
  apiKey: window.__firebase_config?.apiKey || process.env.REACT_APP_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: window.__firebase_config?.authDomain || process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'demo.firebaseapp.com',
  projectId: window.__firebase_config?.projectId || process.env.REACT_APP_FIREBASE_PROJECT_ID || 'demo-project',
  storageBucket: window.__firebase_config?.storageBucket || process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'demo.appspot.com',
  messagingSenderId: window.__firebase_config?.messagingSenderId || process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: window.__firebase_config?.appId || process.env.REACT_APP_FIREBASE_APP_ID || '1:123456789:web:abc123'
};

// Application instance
let app = null;

// Auth instance
let auth = null;

// Auth state listener
let authStateListener = null;

// Token refresh timer
let tokenRefreshTimer = null;

// Current user reference
let currentUser = null;

/**
 * Initialize Firebase App and Auth
 * Supports hot-reload by checking if app already initialized
 */
function initializeFirebase() {
  try {
    if (getApps().length > 0) {
      app = getApp();
    } else {
      app = initializeApp(firebaseConfig);
    }

    auth = getAuth(app);

    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_AUTH_EMULATOR === 'true') {
      connectAuthEmulator(auth, 'http://localhost:9099');
    }

    return app;
  } catch (error) {
    return null;
  }
}

/**
 * Get or refresh Firebase ID Token
 * Automatically handles token refresh
 *
 * @returns {Promise<string>} Firebase ID Token
 */
export async function getFirebaseToken() {
  if (!auth) {
    initializeFirebase();
  }

  if (!auth || !currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // Force refresh to get latest token
    const token = await getIdToken(currentUser, true);
    return token;
  } catch (error) {
    console.error('❌ Failed to get Firebase token:', error);
    throw error;
  }
}

/**
 * Get current user
 * @returns {object|null} Current user or null
 */
export function getCurrentUser() {
  return currentUser;
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated() {
  return currentUser !== null;
}

/**
 * Sign in anonymously
 * Creates a Firebase user without requiring credentials
 *
 * @returns {Promise<object>} User credential
 */
export async function signInAnonymouslyUser() {
  if (!auth) {
    initializeFirebase();
  }

  if (!auth) {
    throw new Error('Firebase not initialized');
  }

  try {
    const result = await signInAnonymously(auth);
    currentUser = result.user;
    console.log('✅ Anonymous sign-in success:', currentUser.uid);
    return result;
  } catch (error) {
    console.error('❌ Anonymous sign-in failed:', error);
    throw error;
  }
}

/**
 * Sign in with Google
 * Uses popup-based OAuth flow
 *
 * @returns {Promise<object>} User credential
 */
export async function signInWithGoogle() {
  if (!auth) {
    initializeFirebase();
  }

  if (!auth) {
    throw new Error('Firebase not initialized');
  }

  try {
    const provider = new GoogleAuthProvider();
    // Add additional scopes if needed
    provider.addScope('profile');
    provider.addScope('email');

    const result = await signInWithPopup(auth, provider);
    currentUser = result.user;
    console.log('✅ Google sign-in success:', currentUser.uid);
    return result;
  } catch (error) {
    console.error('❌ Google sign-in failed:', error);
    throw error;
  }
}

export async function signInWithEmailCode(email, code) {
  const apiUrl = window.__api_base_url || (process.env.NODE_ENV === 'production' ? 'https://openyc-api-backend.fly.dev' : 'http://localhost:8081');

  const response = await fetch(`${apiUrl}/api/v1/auth/verify-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, code }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '验证失败');
  }

  const data = await response.json();

  localStorage.setItem('auth_token', data.token);
  localStorage.setItem('user_email', email);

  currentUser = {
    uid: data.user.uid,
    email: data.user.email,
    displayName: data.user.displayName,
    photoURL: data.user.photoURL,
    providerData: [{ providerId: 'email' }]
  };

  console.log('✅ Email verification successful:', currentUser.uid);
  return { user: currentUser };
}

/**
 * Send Email Verification Code
 * Sends a 6-digit verification code to user's email via backend API
 *
 * @param {string} email - User email address
 * @returns {Promise<void>}
 */
export async function sendEmailVerificationCode(email) {
  if (!email) {
    throw new Error('Email is required');
  }

  const apiUrl = window.__api_base_url || (process.env.NODE_ENV === 'production' ? 'https://openyc-api-backend.fly.dev' : 'http://localhost:8081');
  
  console.log('[EmailAuth] apiUrl:', apiUrl);
  console.log('[EmailAuth] window.__api_base_url:', window.__api_base_url);
  console.log('[EmailAuth] Sending request to:', `${apiUrl}/api/v1/auth/send-code`);

  try {
    const response = await fetch(`${apiUrl}/api/v1/auth/send-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    console.log('[EmailAuth] Response status:', response.status);
    console.log('[EmailAuth] Response ok:', response.ok);

    if (!response.ok) {
      let errorMessage = '发送验证码失败';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    console.log('[EmailAuth] Verification code sent to:', email);
  } catch (fetchError) {
    console.error('[EmailAuth] Fetch error:', fetchError);
    throw fetchError;
  }
}

/**
 * Sign out from Firebase
 * Clears local user state
 */
export async function signOut() {
  if (!auth) {
    initializeFirebase();
  }

  try {
    if (auth.currentUser) {
      await firebaseSignOut(auth);
    }
    currentUser = null;
    clearTokenRefreshTimer();
    console.log('✅ Sign out success');
  } catch (error) {
    console.error('❌ Sign out failed:', error);
    throw error;
  }
}

/**
 * Listen for authentication state changes
 * Sets up global auth state listener
 *
 * @param {Function} callback - Called with user object or null
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  if (!auth) {
    initializeFirebase();
  }

  if (!auth) {
    // Firebase not available, return dummy unsubscribe
    console.warn('⚠️ Firebase not initialized, auth state change not available');
    return () => {};
  }

  // Set up listener if not already set
  if (!authStateListener) {
    authStateListener = onAuthStateChanged(auth, async (user) => {
      currentUser = user;

      if (user) {
        // Set up automatic token refresh
        setupTokenRefresh(user);

        // Get and store token
        try {
          const token = await getIdToken(user);
          localStorage.setItem('firebase_token', token);
        } catch (error) {
          console.warn('⚠️ Failed to get initial token:', error);
        }
      } else {
        localStorage.removeItem('firebase_token');
        clearTokenRefreshTimer();
      }

      // Call user's callback
      if (callback) {
        callback(user);
      }
    });
  }

  // Return unsubscribe function
  return () => {
    if (authStateListener && callback) {
      // Note: We don't actually unsubscribe from Firebase
      // because we want to maintain the global listener
      callback = null;
    }
  };
}

/**
 * Set up automatic token refresh
 * Tokens expire after 1 hour, refresh 5 minutes before expiry
 */
function setupTokenRefresh(user) {
  clearTokenRefreshTimer();

  user.getIdTokenResult()
    .then((idTokenResult) => {
      // Calculate refresh time (expiration - 5 minutes)
      const expirationTime = new Date(idTokenResult.expirationTime).getTime();
      const refreshTime = expirationTime - (5 * 60 * 1000);
      const timeUntilRefresh = refreshTime - Date.now();

      if (timeUntilRefresh > 0) {
        tokenRefreshTimer = setTimeout(async () => {
          try {
            const newToken = await getIdToken(user, true);
            localStorage.setItem('firebase_token', newToken);
            console.log('🔄 Token refreshed');
            setupTokenRefresh(user); // Set up next refresh
          } catch (error) {
            console.error('❌ Token refresh failed:', error);
          }
        }, timeUntilRefresh);
      } else {
        // Token expires soon or already expired, refresh immediately
        setupTokenRefresh(user);
      }
    })
    .catch((error) => {
      console.error('❌ Failed to get token result:', error);
    });
}

/**
 * Clear token refresh timer
 */
function clearTokenRefreshTimer() {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}

/**
 * Initialize auth on module load
 * Sets up global listener immediately
 */
initializeFirebase();

// Export auth instance for advanced use
export function getAuthInstance() {
  return auth;
}

export function getAppInstance() {
  return app;
}
